import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { Plus, Search, Star, Check, X, Edit, Trash2, MessageSquare, Video } from "lucide-react";

interface Review {
  id: string;
  tripId?: string;
  destinationId?: string;
  guideId?: string;
  customerName: string;
  customerLocation?: string;
  rating: number;
  title?: string;
  content: string;
  tripDate?: string;
  isFeatured: boolean;
  isVideo: boolean;
  videoUrl?: string;
  isApproved: boolean;
  createdAt: string;
  tripTitle?: string;
  guideName?: string;
  destinationName?: string;
}

export default function ReviewsPage() {
  const [search, setSearch] = useState("");
  const [filterApproved, setFilterApproved] = useState<"all" | "approved" | "pending">("all");
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-reviews"],
    queryFn: () => api.get<ApiResponse<Review[]>>("/api/admin/reviews"),
  });

  const toggleApprovalMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/reviews/${id}/toggle-approval`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-reviews"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/reviews/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-reviews"] });
    },
  });

  const reviews = data?.data || [];

  // Filter reviews
  const filteredReviews = reviews.filter((review) => {
    const matchesSearch =
      review.customerName.toLowerCase().includes(search.toLowerCase()) ||
      review.content.toLowerCase().includes(search.toLowerCase()) ||
      review.title?.toLowerCase().includes(search.toLowerCase()) ||
      review.tripTitle?.toLowerCase().includes(search.toLowerCase());

    const matchesApproved =
      filterApproved === "all" ||
      (filterApproved === "approved" && review.isApproved) ||
      (filterApproved === "pending" && !review.isApproved);

    return matchesSearch && matchesApproved;
  });

  const handleDelete = (id: string) => {
    if (confirm("Opravdu chcete smazat tuto recenzi?")) {
      deleteMutation.mutate(id);
    }
  };

  if (error) {
    return (
      <div className="card text-center text-error-600">
        Chyba při načítání recenzí. Zkuste to prosím znovu.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Recenze
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Spravujte recenze zákazníků
          </p>
        </div>
        <Link to="/reviews/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          Nová recenze
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Hledat recenze..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterApproved("all")}
              className={`btn-sm ${filterApproved === "all" ? "btn-primary" : "btn-ghost"}`}
            >
              Všechny
            </button>
            <button
              onClick={() => setFilterApproved("approved")}
              className={`btn-sm ${filterApproved === "approved" ? "btn-primary" : "btn-ghost"}`}
            >
              <Check className="h-4 w-4" />
              Schválené
            </button>
            <button
              onClick={() => setFilterApproved("pending")}
              className={`btn-sm ${filterApproved === "pending" ? "btn-primary" : "btn-ghost"}`}
            >
              <X className="h-4 w-4" />
              Čekající
            </button>
          </div>
        </div>
      </div>

      {/* Reviews list */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
        </div>
      ) : filteredReviews.length === 0 ? (
        <div className="card flex h-64 flex-col items-center justify-center text-gray-500">
          <MessageSquare className="mb-2 h-12 w-12 text-gray-300" />
          <p>Žádné recenze nenalezeny</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReviews.map((review) => (
            <div
              key={review.id}
              className={`card p-0 overflow-hidden ${!review.isApproved ? "border-l-4 border-l-warning-500" : ""}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {review.customerName}
                      </span>
                      {review.customerLocation && (
                        <span className="text-sm text-gray-500">
                          z {review.customerLocation}
                        </span>
                      )}
                      {/* Rating stars */}
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
                          />
                        ))}
                      </div>
                      {review.isFeatured && (
                        <span className="badge-primary text-xs">Featured</span>
                      )}
                      {review.isVideo && (
                        <span className="badge bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 text-xs">
                          <Video className="h-3 w-3 mr-1" />
                          Video
                        </span>
                      )}
                      {!review.isApproved && (
                        <span className="badge bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400 text-xs">
                          Čeká na schválení
                        </span>
                      )}
                    </div>

                    {review.title && (
                      <h3 className="mt-2 font-medium text-gray-900 dark:text-white">
                        {review.title}
                      </h3>
                    )}

                    <p className="mt-2 text-gray-600 dark:text-gray-400 line-clamp-2">
                      {review.content}
                    </p>

                    <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                      {review.tripTitle && (
                        <span>Zájezd: {review.tripTitle}</span>
                      )}
                      {review.guideName && (
                        <span>Průvodce: {review.guideName}</span>
                      )}
                      {review.destinationName && (
                        <span>Destinace: {review.destinationName}</span>
                      )}
                      {review.tripDate && (
                        <span>Datum cesty: {new Date(review.tripDate).toLocaleDateString("cs-CZ")}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => toggleApprovalMutation.mutate(review.id)}
                      disabled={toggleApprovalMutation.isPending}
                      className={`btn-sm ${review.isApproved ? "btn-ghost text-warning-600" : "btn-primary"}`}
                      title={review.isApproved ? "Zrušit schválení" : "Schválit"}
                    >
                      {review.isApproved ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                    </button>
                    <Link
                      to={`/reviews/${review.id}`}
                      className="btn-ghost btn-sm"
                    >
                      <Edit className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => handleDelete(review.id)}
                      disabled={deleteMutation.isPending}
                      className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
